import { ALLOWED_HOSTS } from './document-hosts';
import { docViewerKind, nameExtension, type DocViewerKind } from './doc-viewer';

/**
 * A link in rendered text that points at a FILE on Wix media — opened in the in-app doc viewer rather than
 * raw in a browser tab.
 *
 * Why: Wix media serves text with no charset, so a Hebrew `.md` opened raw reads as mojibake, and markdown
 * is shown as source. The doc viewer decodes UTF-8 and renders the kind. Used by both the markdown link
 * renderer (`lib/markdown-link-target.ts`) and the plain-text one (`components/LinkifiedText.tsx`).
 */
export interface MediaFileLink {
  url: string;
  fileName: string;
  kind: DocViewerKind;
}

/** The media-file hosts: the document allowlist minus `.wixel.com`, whose subdomains serve PAGES (the
 *  design platform's viewer/editor), not file bytes — a wixel link stays an ordinary outbound link. */
export const MEDIA_FILE_HOSTS: readonly string[] = ALLOWED_HOSTS.filter((suffix) => suffix !== '.wixel.com');

/** A label that reads as a file name: one line, no path or scheme, and an extension. Agents often write
 *  `[report.md](https://…usrfiles.com/ugd/…txt)` — the label is the real name, the URL's is a storage id. */
function labelAsFileName(label: string | undefined | null): string | null {
  const trimmed = (label ?? '').trim();
  if (!trimmed || trimmed.length > 200) return null;
  if (/[\n/\\:]/.test(trimmed)) return null;
  const ext = nameExtension(trimmed);
  return /^[a-z0-9]{1,8}$/.test(ext) ? trimmed : null;
}

function lastPathSegment(url: URL): string {
  const segments = url.pathname.split('/').filter(Boolean);
  const last = segments[segments.length - 1] ?? '';
  try {
    return decodeURIComponent(last);
  } catch {
    return last;
  }
}

export function mediaFileLink(href: string | undefined | null, label?: string | null): MediaFileLink | null {
  if (!href) return null;
  let parsed: URL;
  try {
    parsed = new URL(href);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:') return null;
  const host = parsed.hostname.toLowerCase();
  if (!MEDIA_FILE_HOSTS.some((suffix) => host.endsWith(suffix))) return null;

  // The label wins when it names a file the viewer can show; otherwise the URL's own last segment.
  const fromLabel = labelAsFileName(label);
  if (fromLabel) {
    const kind = docViewerKind(fromLabel, href);
    if (kind) return { url: href, fileName: fromLabel, kind };
  }
  const fileName = lastPathSegment(parsed);
  if (!fileName) return null;
  const kind = docViewerKind(fileName, href);
  return kind ? { url: href, fileName, kind } : null;
}
